import { numberToWords } from "./numbers.js";
import type { DirectorNote, IndexedWord, ParsedScript } from "./types.js";

/**
 * A word is a maximal run of letters, digits and apostrophes. Apostrophes are
 * collapsed (so "don't" → "dont") while any other punctuation — hyphens,
 * slashes, whitespace — separates words ("well-known" → "well", "known"). A
 * comma between two digits is kept as a thousands separator ("1,024").
 */
const WORD_RE = /[\p{L}\p{N}']+(?:(?<=\p{N}),\p{N}+)*/gu;
const BRACKET_RE = /\[[^\]]*\]/gu;
const PURE_DIGITS_RE = /^\d+$/;

/**
 * Normalise a single raw word token into zero or more index words. Apostrophes
 * are dropped and the token lowercased; a pure numeral (after removing grouping
 * commas) is expanded to its spoken words (FR-15).
 */
function normalizeToken(raw: string): string[] {
  const cleaned = raw.replace(/,/g, "");
  if (PURE_DIGITS_RE.test(cleaned)) {
    const expanded = numberToWords(Number(cleaned));
    if (expanded) return expanded;
  }
  const normalized = raw.replace(/[',]/g, "").toLowerCase();
  return normalized.length === 0 ? [] : [normalized];
}

interface Span {
  start: number;
  end: number;
}

/** Whether `offset` falls within any of the (sorted or unsorted) note spans. */
function inAnySpan(offset: number, spans: readonly Span[]): boolean {
  for (const s of spans) {
    if (offset >= s.start && offset < s.end) return true;
  }
  return false;
}

/**
 * Collect director notes (FR-14): every `[bracketed]` span plus every line
 * whose first non-whitespace character is `#`. Hash lines take precedence, so
 * a bracket inside a hash line is not counted twice.
 */
function collectNotes(source: string): { notes: DirectorNote[]; spans: Span[] } {
  const notes: DirectorNote[] = [];

  // Hash lines first, so bracket scanning can skip anything they cover.
  const hashSpans: Span[] = [];
  let lineStart = 0;
  for (const line of source.split("\n")) {
    if (/^\s*#/.test(line)) {
      const span = { start: lineStart, end: lineStart + line.length };
      hashSpans.push(span);
      notes.push({
        kind: "hash",
        text: line.replace(/^\s*#/, "").trim(),
        sourceStart: span.start,
        sourceEnd: span.end,
      });
    }
    lineStart += line.length + 1; // +1 for the consumed "\n"
  }

  const bracketSpans: Span[] = [];
  for (const match of source.matchAll(BRACKET_RE)) {
    if (inAnySpan(match.index, hashSpans)) continue;
    const span = { start: match.index, end: match.index + match[0].length };
    bracketSpans.push(span);
    notes.push({
      kind: "bracket",
      text: match[0].slice(1, -1).trim(),
      sourceStart: span.start,
      sourceEnd: span.end,
    });
  }

  notes.sort((a, b) => a.sourceStart - b.sourceStart);
  return { notes, spans: [...hashSpans, ...bracketSpans] };
}

/**
 * Normalise arbitrary text into the same word stream the alignment index uses —
 * lowercased, punctuation stripped, numerals expanded. Used to tokenise
 * recognised speech so it is comparable to the script index.
 */
export function normalizeToWords(text: string): string[] {
  const out: string[] = [];
  for (const match of text.matchAll(WORD_RE)) {
    out.push(...normalizeToken(match[0]));
  }
  return out;
}

export function parseScript(source: string): ParsedScript {
  const { notes, spans } = collectNotes(source);
  const words: IndexedWord[] = [];

  for (const match of source.matchAll(WORD_RE)) {
    if (inAnySpan(match.index, spans)) continue;
    const raw = match[0];
    const sourceStart = match.index;
    const sourceEnd = sourceStart + raw.length;
    for (const normalized of normalizeToken(raw)) {
      words.push({ index: words.length, normalized, sourceStart, sourceEnd });
    }
  }

  return { words, notes, source };
}
